import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

export interface Sponsor {
  id: number;
  company_name: string;
  logo_url: string;
  banner_url: string;
  website_url: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  ad_type: string;
  placement: string;
  active: boolean;
  start_date: string;
  end_date: string;
  notes: string;
  created_at: string;
  placements?: string[];
}

/**
 * Los tres tamaños de banner que se venden. El tamaño se guarda en `ad_type`:
 * "cintillo" y "mediano" son nuevos; "banner" (el valor por defecto) y los
 * tipos antiguos (sidebar, hero…) se muestran como pequeño, igual que antes.
 */
export type AdFormat = "cintillo" | "mediano" | "pequeno";

export const AD_FORMATS: Record<AdFormat, { label: string; width: number; height: number; where: string }> = {
  cintillo: { label: "Cintillo horizontal", width: 728, height: 90, where: "Arriba de cada sección" },
  mediano: { label: "Banner mediano", width: 300, height: 250, where: "Columna derecha" },
  pequeno: { label: "Banner pequeño", width: 300, height: 125, where: "Columna derecha" },
};

export function adFormatOf(adType?: string): AdFormat {
  return adType === "cintillo" || adType === "mediano" ? adType : "pequeno";
}

// Minúsculas y sin acentos: el admin guarda "aerolineas" y las páginas piden "Aerolíneas".
const normalize = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").trim().toLowerCase();

/** El admin guarda `placement` como JSON ('["aerolineas","turismo"]'); los registros viejos, como texto simple. */
function placementsOf(s: Sponsor): string[] {
  if (Array.isArray(s.placements) && s.placements.length > 0) return s.placements;
  if (!s.placement) return [];
  try {
    const parsed = JSON.parse(s.placement);
    if (Array.isArray(parsed)) return parsed.map(String);
  } catch {
    // texto simple
  }
  return [s.placement];
}

/**
 * `kind` separa los cintillos (728×90) de los banners de columna, para que un
 * arte horizontal nunca caiga en una tarjeta cuadrada ni al revés.
 */
export function useSponsors(placement?: string, kind: "bloque" | "cintillo" = "bloque") {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSponsors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from("sponsors")
        .select("*")
        .eq("active", true)
        .order("created_at", { ascending: false });

      const { data, error: supaError } = await query;

      if (supaError) throw supaError;

      let filtered: Sponsor[] = data || [];
      if (placement) {
        const wanted = normalize(placement);
        filtered = filtered.filter((s) => {
          const places = placementsOf(s).map(normalize);
          return places.includes(wanted) || places.includes("todas");
        });
      }
      filtered = filtered.filter((s) =>
        kind === "cintillo" ? adFormatOf(s.ad_type) === "cintillo" : adFormatOf(s.ad_type) !== "cintillo",
      );

      setSponsors(filtered);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar sponsors");
    } finally {
      setLoading(false);
    }
  }, [placement, kind]);

  useEffect(() => {
    fetchSponsors();
  }, [fetchSponsors]);

  return { sponsors, loading, error, refetch: fetchSponsors };
}
